/**
 * Política de Privacidade — Sistema de Centros de Treinamento Meiuca (B2B).
 *
 *  RASCUNHO PRELIMINAR — REVISÃO JURÍDICA OBRIGATÓRIA antes da publicação oficial.
 *
 * Endpoint público esperado: https://meiucaapp.com.br/centros-de-treinamento/privacidade
 *
 * Cobre o tratamento de dados pessoais de operadores e Alunos realizados pela
 * Meiuca no contexto do Sistema de Centros de Treinamento, incluindo o
 * compartilhamento com o ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.
 * para cumprimento das exigências regulatórias do BACEN.
 *
 * Migrado de `meiuca-site` (Next.js) para `meiuca-app-web-react` (React Router).
 */

import { Body, H2, H3 } from '@meiuca/ui-components'
import { LegalPageLayout } from '../components/LegalPageLayout'

const ctRelatedLinks = [
  { href: '/centros-de-treinamento/termos', label: 'Termos do Sistema de CT' },
  { href: '/centros-de-treinamento/privacidade', label: 'Privacidade do Sistema de CT' },
  { href: '/termos', label: 'Termos do app Meiuca (consumidor)' }
]

export function CentrosDeTreinamentoPrivacidadePage() {
  return (
    <LegalPageLayout
      title="Política de Privacidade — Centros de Treinamento"
      documentTitle="Política de Privacidade — Centros de Treinamento — Meiuca"
      lastUpdated="20 de maio de 2026"
      relatedLinks={ctRelatedLinks}
    >
      <Body>
        Esta Política de Privacidade descreve como a Meiuca trata os dados pessoais
        coletados no <strong>Sistema de Centros de Treinamento</strong>, em conformidade
        com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD) e com a
        Resolução Conjunta nº 16/2025 do Banco Central do Brasil. Ao aderir ao Sistema,
        o CT concorda com as práticas descritas aqui.
      </Body>

      <section>
        <H2 className="text-xl md:text-2xl mt-8 mb-3">1. Quem somos</H2>
        <Body>
          O Sistema de Centros de Treinamento é operado por{' '}
          <strong>Rafael Balmant Letro — ME</strong>, inscrita no CNPJ sob nº{' '}
          <strong>27.422.545/0001-42</strong> e Inscrição Municipal nº{' '}
          <strong>00098036</strong>, com sede na{' '}
          <strong>Rua São Marcos, 370 — Bairro São Vito — Americana/SP — CEP 13472-200</strong>.
          Para fins da LGPD, somos o <strong>controlador</strong> dos dados pessoais
          de operadores do CT e <strong>operador</strong> dos dados de Alunos que o CT
          nos confiar para fins de execução do serviço.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">2. Quais dados coletamos</H2>

        <H3 className="text-lg mt-4 mb-2">2.1 Dados dos operadores do CT</H3>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Nome completo, e-mail, telefone, senha (hash) e fotografia (opcional).</li>
          <li>Cargo/função no CT e nível de acesso.</li>
          <li>Registro profissional (ex.: CREF), quando aplicável.</li>
          <li>Logs técnicos de acesso (data/hora, IP, navegador, ações realizadas).</li>
        </ul>

        <H3 className="text-lg mt-6 mb-2">2.2 Dados do CT e do titular</H3>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Razão social ou nome completo (PF), CPF ou CNPJ, data de nascimento (PF).</li>
          <li>E-mail e telefone de cobrança.</li>
          <li>Endereço completo (rua, número, complemento, bairro, CEP, cidade, estado).</li>
          <li>Renda mensal (PF) ou faturamento mensal (PJ).</li>
          <li>Chave Pix para recebimento de saques (tipo e identificador, armazenado mascarado).</li>
        </ul>

        <H3 className="text-lg mt-6 mb-2">2.3 Dados gerados pelo uso</H3>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Turmas, contratos, planos, locais de treino e configurações de meios de pagamento.</li>
          <li>Mensalidades, cobranças, refunds, saldos, ledger e extratos.</li>
          <li>Ocorrências registradas relacionadas a Alunos.</li>
          <li>Comunicações via suporte e notificações enviadas.</li>
        </ul>

        <H3 className="text-lg mt-6 mb-2">2.4 Dados de Alunos (sob controle do CT)</H3>
        <Body>
          O CT pode cadastrar Alunos e seus responsáveis com nome, telefone, e-mail, CPF
          (quando aplicável) e dados de contrato (plano, valor, data de início,
          modalidade). A Meiuca atua como <strong>operadora</strong> desses dados,
          processando-os exclusivamente conforme orientação do CT (controlador) e estes
          Termos. O CT é responsável por obter as bases legais adequadas (consentimento
          do responsável legal, no caso de menores).
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">
          3. Compartilhamento com o ASAAS
        </H2>
        <Body>
          Para que o CT possa receber mensalidades e cobranças online, é necessário abrir
          uma <strong>Subconta no ASAAS</strong>. Para tanto, compartilhamos com o ASAAS:
        </Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-3">
          <li>Razão social ou nome completo, CPF/CNPJ, e-mail e telefone de cobrança;</li>
          <li>Data de nascimento (PF);</li>
          <li>Renda/faturamento mensal;</li>
          <li>Endereço completo;</li>
          <li>Chave Pix e dados de saque.</li>
        </ul>
        <Body className="mt-3">
          O ASAAS, na qualidade de instituição de pagamento autorizada pelo BACEN, atua
          como <strong>controlador autônomo</strong> desses dados para cumprimento das
          obrigações de KYC, PLD/FT, prevenção a fraude, retenções legais e demais
          exigências regulatórias. O tratamento realizado pelo ASAAS é regido pela{' '}
          <a
            href="https://www.asaas.com/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary hover:underline"
          >
            Política de Privacidade do ASAAS
          </a>
          .
        </Body>
        <Body className="mt-3">
          Adicionalmente, em cada cobrança Pix ou boleto emitido para o Aluno, transmitimos
          ao ASAAS os dados mínimos do Aluno pagador (nome, telefone, CPF) para atender
          às exigências regulatórias do BACEN para o processamento.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">4. Para que usamos esses dados</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Autenticar operadores e manter sessões seguras.</li>
          <li>Operar as funcionalidades do Sistema contratadas pelo CT.</li>
          <li>Processar mensalidades, cobranças, refunds e saques via ASAAS.</li>
          <li>Comunicar eventos relevantes (aprovação de Subconta, falhas, webhooks).</li>
          <li>Atender obrigações legais, fiscais, regulatórias e contratuais.</li>
          <li>Investigar fraudes, abuso e proteger a integridade da plataforma.</li>
          <li>Melhorar o produto a partir de métricas agregadas e despersonalizadas.</li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">5. Bases legais (LGPD)</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>
            <strong>Execução de contrato</strong> (art. 7º, V) — para operar o Sistema
            e processar pagamentos.
          </li>
          <li>
            <strong>Cumprimento de obrigação legal/regulatória</strong> (art. 7º, II) —
            para atender ao BACEN, à Receita Federal e à LGPD.
          </li>
          <li>
            <strong>Legítimo interesse</strong> (art. 7º, IX) — para prevenção a fraude,
            segurança da plataforma e melhoria do produto.
          </li>
          <li>
            <strong>Consentimento</strong> (art. 7º, I) — em hipóteses específicas
            indicadas no momento da coleta, especialmente para Alunos menores de idade,
            obtido pelo CT junto ao responsável legal.
          </li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">6. Com quem mais compartilhamos</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>
            <strong>Provedores de infraestrutura</strong> (hospedagem em nuvem, banco de
            dados, envio de e-mail e SMS), sob contrato de operação.
          </li>
          <li>
            <strong>Autoridades públicas</strong>, quando exigido por lei ou ordem judicial.
          </li>
          <li>
            <strong>Parceiros estritamente necessários</strong> para a execução do serviço
            contratado pelo CT, sempre com base legal apropriada.
          </li>
        </ul>
        <Body className="mt-3">
          Não vendemos seus dados nem os usamos para perfilamento publicitário próprio
          ou de terceiros.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">7. Retenção</H2>
        <Body>
          Dados cadastrais do CT são mantidos enquanto a adesão estiver ativa. Após a
          rescisão, dados são mantidos pelo período mínimo necessário para o cumprimento
          de obrigações fiscais, contratuais e regulatórias (em regra, 5 anos para
          registros financeiros). Dados sensíveis para KYC e prevenção à lavagem ficam
          sob a custódia do ASAAS pelos prazos exigidos pelo BACEN.
        </Body>
        <Body className="mt-3">
          Ocorrências registradas no Sistema são mantidas enquanto existir vínculo
          contratual entre o CT e o Aluno e, após o encerramento, pelo prazo legal
          aplicável a possível defesa em juízo.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">8. Direitos dos titulares</H2>
        <Body>A LGPD (art. 18) garante aos titulares:</Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-2">
          <li>Confirmar a existência e acessar os dados.</li>
          <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
          <li>Solicitar anonimização, bloqueio ou eliminação de dados desnecessários.</li>
          <li>Pedir portabilidade para outro fornecedor.</li>
          <li>Revogar consentimento, quando aplicável.</li>
          <li>Apresentar reclamação à ANPD.</li>
        </ul>
        <Body className="mt-4">
          Solicitações de Alunos cadastrados pelo CT devem ser direcionadas primeiramente
          ao próprio CT, que é o controlador desses dados. A Meiuca apoia o atendimento
          na qualidade de operadora.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">9. Segurança</H2>
        <Body>
          A comunicação com o Sistema é feita por HTTPS. Senhas são armazenadas com hash.
          Tokens de autenticação são guardados em armazenamento seguro do navegador.
          Adotamos controles de acesso por função (RBAC) e monitoramento de eventos de
          segurança. Em caso de incidente que afete os dados, comunicaremos o CT, os
          titulares e a ANPD nos prazos legais.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">10. Alterações nesta política</H2>
        <Body>
          Esta política pode ser atualizada para refletir mudanças no produto, na
          regulamentação ou na infraestrutura. Mudanças relevantes serão comunicadas
          ao CT por e-mail ou por aviso no Sistema.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">11. Contato</H2>
        <Body>
          Encarregado pelo tratamento de dados (DPO):{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>
          . Para assuntos sobre o tratamento realizado pelo ASAAS, contate diretamente o
          DPO da instituição:{' '}
          <a
            href="mailto:dpo@asaas.com.br"
            className="text-brand-primary hover:underline"
          >
            dpo@asaas.com.br
          </a>
          .
        </Body>
      </section>
    </LegalPageLayout>
  )
}
